import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Associate } from '../../types';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, addDoc, updateDoc, collection } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';
import RichTextEditor from '../../components/RichTextEditor';

const generateSlug = (title: string) => {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
};

const UploadIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const FileIcon: React.FC<{className?: string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>;


const AdminAssociateFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditing = Boolean(id);
    const { showToast } = useToast();
    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    
    const [associate, setAssociate] = useState<Omit<Associate, 'id'>>({
        name: '',
        slug: '',
        logoUrl: '',
        description: '',
        aboutUs: '',
        vision: '',
        mission: '',
        services: '',
        address: '',
        email: '',
        companyProfileUrl: '',
    });
    
    const [profileFile, setProfileFile] = useState<File | null>(null);
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    useEffect(() => {
        if (isEditing && id) {
            const fetchAssociate = async () => {
                const docRef = doc(db, 'associates', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setAssociate(docSnap.data() as Associate);
                } else {
                    showToast('Associate not found.', { type: 'error' });
                    navigate('/admin/associates');
                }
                setIsLoading(false);
            }
            fetchAssociate();
        } else {
            setIsLoading(false);
        }
    }, [id, isEditing, navigate, showToast]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setAssociate(prev => ({ ...prev, [name]: value }));
        if (name === 'name' && !isEditing) {
            setAssociate(prev => ({ ...prev, slug: generateSlug(value) }));
        }
    };
    
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'profile') => {
        if (e.target.files && e.target.files.length > 0) {
            if (type === 'logo') setLogoFile(e.target.files[0]);
            if (type === 'profile') setProfileFile(e.target.files[0]);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            let logoUrl = associate.logoUrl;
            let profileUrl = associate.companyProfileUrl;

            if (logoFile) {
                setIsUploadingLogo(true);
                const storageRef = ref(storage, `associates/logos/${Date.now()}_${logoFile.name}`);
                await uploadBytes(storageRef, logoFile);
                logoUrl = await getDownloadURL(storageRef);
                setIsUploadingLogo(false);
            }
            
            if (profileFile) {
                setIsUploadingProfile(true);
                const storageRef = ref(storage, `associates/profiles/${Date.now()}_${profileFile.name}`);
                await uploadBytes(storageRef, profileFile);
                profileUrl = await getDownloadURL(storageRef);
                setIsUploadingProfile(false);
            }

            const dataToSave = { 
                ...associate,
                logoUrl,
                companyProfileUrl: profileUrl,
                slug: isEditing ? associate.slug : generateSlug(associate.name) 
            };
            if (isEditing && id) {
                await updateDoc(doc(db, 'associates', id), dataToSave);
                showToast('Associate updated successfully!', { type: 'success' });
            } else {
                await addDoc(collection(db, 'associates'), dataToSave);
                showToast('Associate added successfully!', { type: 'success' });
            }
            navigate('/admin/associates');
        } catch (error) {
            showToast('Failed to save associate.', { type: 'error' });
            setIsSaving(false);
        }
    };
    
    if (isLoading) {
        return <div className="text-center p-8">Loading associate details...</div>;
    }

    return (
        <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">{isEditing ? 'Edit Associate' : 'Add New Associate'}</h2>
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        {/* Core Info */}
                        <div className="pb-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-800">Core Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Company Name</label>
                                    <input type="text" name="name" id="name" value={associate.name} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                                </div>
                                <div>
                                    <label htmlFor="slug" className="block text-sm font-medium text-gray-700">URL Slug</label>
                                    <input type="text" name="slug" id="slug" value={associate.slug} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"/>
                                </div>
                            </div>
                             <div className="mt-4">
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Short Description (for list page)</label>
                                <textarea name="description" id="description" rows={2} value={associate.description} onChange={handleChange} required className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                            </div>
                        </div>

                        {/* Profile Content */}
                        <div className="pb-4 border-b">
                             <h3 className="text-lg font-semibold text-gray-800">Profile Content</h3>
                             <div className="mt-2 space-y-4">
                                <div>
                                    <label htmlFor="aboutUs" className="block text-sm font-medium text-gray-700">About Us</label>
                                    <RichTextEditor value={associate.aboutUs} onChange={(val) => setAssociate(p => ({...p, aboutUs: val}))} />
                                </div>
                                <div>
                                    <label htmlFor="vision" className="block text-sm font-medium text-gray-700">Vision</label>
                                    <textarea name="vision" id="vision" rows={3} value={associate.vision} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                                </div>
                                <div>
                                    <label htmlFor="mission" className="block text-sm font-medium text-gray-700">Mission</label>
                                    <textarea name="mission" id="mission" rows={3} value={associate.mission} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
                                </div>
                                 <div>
                                    <label htmlFor="services" className="block text-sm font-medium text-gray-700">Our Services</label>
                                    <RichTextEditor value={associate.services} onChange={(val) => setAssociate(p => ({...p, services: val}))} />
                                </div>
                             </div>
                        </div>

                        {/* Contact Info */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                 <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                                    <input type="email" name="email" id="email" value={associate.email} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                                </div>
                                <div>
                                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
                                    <input type="text" name="address" id="address" value={associate.address} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"/>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="space-y-6 bg-white p-8 rounded-lg shadow-md h-fit">
                        {/* Logo Uploader */}
                        <div>
                             <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                            <div className="relative w-48 h-48 mx-auto border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 bg-gray-50" onClick={() => document.getElementById('logo-upload')?.click()}>
                                {isUploadingLogo && <div className="absolute inset-0 bg-white/80 z-10 grid place-content-center"><div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div></div>}
                                {associate.logoUrl ? <img src={associate.logoUrl} alt="Logo" className="h-full w-full object-contain p-2"/> : <div className="text-center text-gray-500"><UploadIcon className="mx-auto h-8 w-8"/><span className="mt-2 text-sm">Upload Logo</span></div>}
                            </div>
                            <input id="logo-upload" type="file" className="hidden" accept="image/*" onChange={(e) => handleFileChange(e, 'logo')} />
                        </div>
                        {/* Profile Uploader */}
                        <div className="border-t pt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company Profile (PDF)</label>
                            <div className="relative w-full p-4 border-2 border-dashed rounded-md flex items-center justify-center cursor-pointer hover:border-gray-500 bg-gray-50" onClick={() => document.getElementById('profile-upload')?.click()}>
                                {isUploadingProfile && <div className="absolute inset-0 bg-white/80 z-10 grid place-content-center"><div className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-gray-800"></div></div>}
                                <div className="text-center text-gray-500">
                                    <FileIcon className="mx-auto h-8 w-8" />
                                    {associate.companyProfileUrl && !profileFile && <a href={associate.companyProfileUrl} target="_blank" rel="noreferrer noopener" className="text-xs mt-2 text-indigo-600 hover:underline" onClick={e=>e.stopPropagation()}>View current file</a>}
                                    <p className="mt-1 text-sm">{profileFile ? profileFile.name : 'Click to upload PDF'}</p>
                                </div>
                            </div>
                             <input id="profile-upload" type="file" className="hidden" accept=".pdf" onChange={(e) => handleFileChange(e, 'profile')} />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-4 bg-white p-4 rounded-lg shadow-md sticky bottom-4">
                    <button type="button" onClick={() => navigate('/admin/associates')} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving || isUploadingLogo || isUploadingProfile} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Associate'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminAssociateFormPage;